/**
 * The v2 sidebar (TMMAPP-0150 U2 §B 8, visual contract v2 §3, P:22–73; TMMAPP-0159 §A–B): the
 * wordmark, the five destinations — Home, My Financial Assistant, My Money Meetings, Community, Money
 * Tools — and
 * the Money Tools submenu, open while any tool page shows (a detail page counts as its list), with
 * the tier rules from `navigation.ts` and the count pills on My Accounts and Recurring. Under the
 * nav: the Today block, a guest's quiet own-books link and a NONE tier's Subscribe card (D1, kept);
 * at the foot, Help & feedback. Virtual accounts are reached from Virtual Accounts and Home's Shared
 * with you row; Viewing is removed.
 *
 * Permanent (236 px) at `lg` and up; a temporary drawer of the same width at `md` and below, whose
 * open state, focus and close-on-navigate belong to `AppShell`. `SidebarNavList` is the
 * presentational core (it fetches nothing itself unless `counts` asks for the live count pills),
 * shared with `GatedShell`.
 *
 * The sidebar paints the prototype's `surface2` where this brand measures the sidebar's pairs on it,
 * else `surface` (`sidebarCanvas`, TMMAPP-0151 §A 5): the label canvas whose derived `textLabel` its
 * small labels use — never a raw `text3`, which saas-auth guarantees only at 3:1. Rows keep the
 * prototype's natural line height (a 40 px pitch for a destination, 31 px for a Money Tools row).
 * While the Money Tools submenu is closed, its row carries one rolled-up "{count} to review" pill.
 */
import type { ComponentType, ReactNode } from 'react';
import { Box, Drawer, Link, List, ListItemButton, Typography, useTheme } from '@mui/material';
import type { Theme } from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Me } from '@/api/me';
import { BrandMark } from '@/components/BrandLogo';
import {
  AssistantSparkleNavIcon,
  CommunityNavIcon,
  ExpandIcon,
  HelpNavIcon,
  HomeNavIcon,
  MeetingsIcon,
  ToolsNavIcon,
  type IconProps,
} from '@/components/icons';
import { SubscribeCard } from '@/features/subscribe/SubscribeCard';
import { resolveTokens, TEXT_LABEL } from '@/theme/theme';
import { useBrand } from '@/theme/useBrand';
import {
  activeNav,
  activeTool,
  NAV_ITEMS,
  TOOL_ITEMS,
  visibleTo,
  type NavKey,
  type ToolKey,
} from './navigation';
import { ReviewNavCount } from './NavCounts';
import { currentToolInk, sidebarCanvas, sidebarPaperSx } from './sidebarPaper';
import { TodayBlock } from './TodayBlock';

/** Help & feedback: the assistant page opens the person's Help & feedback session (`?help=1`). */
export const HELP_PATH = '/me/assistant?help=1';

const ICONS: Record<NavKey, ComponentType<IconProps>> = {
  home: HomeNavIcon,
  assistant: AssistantSparkleNavIcon,
  meetings: MeetingsIcon,
  community: CommunityNavIcon,
  tools: ToolsNavIcon,
};

const tokensOf = (theme: Theme) => resolveTokens(theme);

// A destination (visual contract v2 §3): padding 10 × 12, radius 11, gap 12, an 18 px icon,
// 14.5 / 500 in `text2` on the prototype's natural 18 px line (a 40 px pitch); `hover` on hover. The current one (`aria-current="page"`) is `brand` with
// `brandInk` (`brandHover` under the pointer); Money Tools, while one of its pages shows, is `link`.
const NAV_ROW_SX = {
  p: '10px 12px',
  gap: 1.5,
  minHeight: 0,
  borderRadius: '11px',
  fontSize: 14.5,
  lineHeight: '18px',
  fontWeight: 500,
  color: (theme: Theme) => tokensOf(theme).text2,
  '&:hover, &.Mui-focusVisible': { bgcolor: (theme: Theme) => tokensOf(theme).hover },
  '&[data-open]': { color: (theme: Theme) => tokensOf(theme).link },
  '&.Mui-selected, &.Mui-selected.Mui-focusVisible': {
    bgcolor: (theme: Theme) => tokensOf(theme).brand,
    color: (theme: Theme) => tokensOf(theme).brandInk,
  },
  '&.Mui-selected:hover': { bgcolor: (theme: Theme) => tokensOf(theme).brandHover },
  '& [data-nav-icon]': { display: 'flex', flexShrink: 0 },
} as const;

// A Money Tools row (visual contract v2 §3): padding 7 × 10, radius 9, 13.5 in `text2` on a 16 px
// line (a 31 px pitch). The current one is `brand` on `brandSoft` at 600 (`currentToolInk`: `text`
// where a brand doesn't measure 4.5:1 there). Keyboard focus on a row is the `hover` layer, as on
// every list row in the app.
const TOOL_ROW_SX = {
  p: '7px 10px',
  gap: 1,
  minHeight: 0,
  borderRadius: '9px',
  fontSize: 13.5,
  lineHeight: '16px',
  fontWeight: 400,
  color: (theme: Theme) => tokensOf(theme).text2,
  '&:hover, &.Mui-focusVisible': { bgcolor: (theme: Theme) => tokensOf(theme).hover },
  '&.Mui-selected, &.Mui-selected:hover, &.Mui-selected.Mui-focusVisible': {
    bgcolor: (theme: Theme) => tokensOf(theme).brandSoft,
    color: (theme: Theme) => currentToolInk(tokensOf(theme)),
    fontWeight: 600,
  },
} as const;

/** The served mark (else the app's bundled mark, never a typed initial), then the served product name. */
export function SidebarWordmark({ onNavigate }: { onNavigate: () => void }) {
  const tokens = resolveTokens(useTheme());
  const { productName } = useBrand();
  return (
    <Box
      component={RouterLink}
      to="/"
      onClick={onNavigate}
      data-wordmark=""
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        px: '6px',
        color: tokens.link,
        textDecoration: 'none',
        borderRadius: `${tokens.radius.control}px`,
      }}
    >
      <Box component="span" aria-hidden sx={{ display: 'flex', flexShrink: 0 }}>
        <BrandMark size={34} bundledFallback />
      </Box>
      <Typography
        variant="h5"
        component="span"
        sx={{ color: 'inherit', lineHeight: 1.05, minWidth: 0, overflowWrap: 'anywhere' }}
      >
        {productName}
      </Typography>
    </Box>
  );
}

/**
 * The closed Money Tools row's one pill (TMMAPP-0151 §A 4): the sum of its items' "to review"
 * counts — My Accounts' review total, today's only one (TMMAPP-0159 §F 33).
 */
function ToolsRollupCount() {
  return <ReviewNavCount />;
}

function ToolCount({ toolKey }: { toolKey: ToolKey }) {
  if (toolKey === 'accounts') return <ReviewNavCount />;
  return null;
}

export interface SidebarNavListProps {
  /** Every destination (SUBSCRIBER), or the GUEST / NONE set. */
  subscriber: boolean;
  /** A GUEST: Audit is hidden, with the quiet own-books link. */
  guest?: boolean;
  pathname: string;
  /** Any navigation inside the sidebar (closes the temporary drawer). */
  onNavigate: () => void;
  /** Show the live count pill (My Accounts' review total): it reads the API. */
  counts: boolean;
  /** Rendered below the destinations (the real sidebar's Today block). */
  children?: ReactNode;
  /** The nav labels only, behind the blocking subscribe dialog (TMMAPP-0116). */
  bare?: boolean;
}

/** The destinations and the Money Tools submenu — presentational; it fetches nothing itself. */
export function SidebarNavList({
  subscriber,
  guest = false,
  pathname,
  onNavigate,
  counts,
  children,
  bare = false,
}: SidebarNavListProps) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  const tier = { subscriber, guest };
  const current = activeNav(pathname);
  const currentTool = activeTool(pathname);

  return (
    <Box sx={{ minWidth: 0 }}>
      <List
        component="div"
        disablePadding
        sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}
      >
        {NAV_ITEMS.map((item) => {
          const Icon = ICONS[item.key];
          const tools = item.key === 'tools';
          const selected = !tools && current === item.key;
          const open = tools && currentTool !== null;
          return (
            <Box key={item.key} sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <ListItemButton
                component={RouterLink}
                to={item.path}
                selected={selected}
                aria-current={selected ? 'page' : undefined}
                onClick={onNavigate}
                data-nav-item=""
                {...(open ? { 'data-open': '' } : {})}
                sx={NAV_ROW_SX}
              >
                <Box component="span" data-nav-icon="">
                  <Icon />
                </Box>
                <Box component="span" sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
                  {t(`nav.${item.key}`)}
                </Box>
                {/* The submenu closed: its counts, rolled up into one pill (§A 4). */}
                {tools && !open && counts && <ToolsRollupCount />}
                {tools && (
                  <Box component="span" data-nav-icon="">
                    <ExpandIcon size={15} />
                  </Box>
                )}
              </ListItemButton>
              {open && (
                <List
                  component="div"
                  role="group"
                  aria-label={t('nav.tools')}
                  disablePadding
                  data-tools-menu=""
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1px',
                    mt: '2px',
                    mb: '6px',
                    marginInlineStart: '21px',
                    paddingInlineStart: '10px',
                    borderInlineStart: '1px solid',
                    borderColor: tokens.line,
                  }}
                >
                  {TOOL_ITEMS.filter((tool) => visibleTo(tool, tier)).map((tool) => {
                    const toolSelected = currentTool === tool.key;
                    return (
                      <ListItemButton
                        key={tool.key}
                        component={RouterLink}
                        to={tool.path}
                        selected={toolSelected}
                        aria-current={toolSelected ? 'page' : undefined}
                        onClick={onNavigate}
                        data-tool-item=""
                        sx={TOOL_ROW_SX}
                      >
                        <Box
                          component="span"
                          sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}
                        >
                          {t(`toolsNav.${tool.key}`)}
                        </Box>
                        {counts && <ToolCount toolKey={tool.key} />}
                      </ListItemButton>
                    );
                  })}
                </List>
              )}
            </Box>
          );
        })}
      </List>
      {guest && !bare && (
        <Box sx={{ px: 1.5, py: 1 }}>
          <Link
            component={RouterLink}
            to="/me/own-books"
            state={{ from: pathname }}
            variant="body2"
            sx={{ color: tokens.link }}
            onClick={onNavigate}
          >
            {t('guest.ownBooks')}
          </Link>
        </Box>
      )}
      {!subscriber && !guest && !bare && (
        <Box sx={{ py: 1.5 }}>
          <SubscribeCard compact onNavigate={onNavigate} />
        </Box>
      )}
      {children}
    </Box>
  );
}

/** Help & feedback, pinned to the foot (visual contract v2 §3). */
function SidebarFoot({ onNavigate }: { onNavigate: () => void }) {
  const { t } = useTranslation();
  return (
    <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: '2px' }}>
      <ListItemButton
        component={RouterLink}
        to={HELP_PATH}
        onClick={onNavigate}
        sx={{ ...NAV_ROW_SX, fontSize: 14, fontWeight: 400 }}
      >
        <Box component="span" data-nav-icon="">
          <HelpNavIcon />
        </Box>
        {t('shell.helpFeedback')}
      </ListItemButton>
    </Box>
  );
}

/**
 * The sidebar's column (padding 22 × 16, gap 20), the sidebar canvas's label canvas in the drawer too. The
 * whole column is the named `nav` landmark — wordmark, the destinations and the foot — so
 * no sidebar content sits outside a landmark.
 */
export function SidebarColumn({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  return (
    <Box
      component="nav"
      aria-label={t('shell.navLabel')}
      data-sidebar=""
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2.5,
        p: '22px 16px',
        minHeight: '100%',
        boxSizing: 'border-box',
        [TEXT_LABEL]: tokens.textLabel(sidebarCanvas(tokens)),
      }}
    >
      {children}
    </Box>
  );
}

interface SidebarProps {
  me: Me;
  /** `lg` and up: the permanent sidebar; below, the temporary drawer. */
  wide: boolean;
  open: boolean;
  onClose: () => void;
  /** Any navigation inside the sidebar (closes the temporary drawer). */
  onNavigate: () => void;
}

function SidebarContent({ me, onNavigate }: SidebarProps) {
  const { pathname } = useLocation();
  return (
    <SidebarColumn>
      <SidebarWordmark onNavigate={onNavigate} />
      <SidebarNavList
        subscriber={me.tier === 'SUBSCRIBER'}
        guest={me.tier === 'GUEST'}
        pathname={pathname}
        onNavigate={onNavigate}
        counts
      >
        <TodayBlock />
      </SidebarNavList>
      <SidebarFoot onNavigate={onNavigate} />
    </SidebarColumn>
  );
}

export function Sidebar(props: SidebarProps) {
  const { wide, open, onClose } = props;
  if (wide) {
    return (
      <Drawer
        variant="permanent"
        sx={(theme) => ({
          flexShrink: 0,
          // The surface and the rule sit on the in-flow root, which stretches the whole page, so
          // they run to the bottom of a long page; the fixed paper only carries the content.
          ...sidebarPaperSx(theme),
          '& .MuiDrawer-paper': { ...sidebarPaperSx(theme), borderInlineEnd: 'none' },
        })}
      >
        <SidebarContent {...props} />
      </Drawer>
    );
  }
  // `md` and below (visual contract v2 §15): the same sidebar as a temporary drawer, at the dialog
  // elevation. The modal moves focus into it and returns it to the menu button on close.
  return (
    <Drawer
      variant="temporary"
      open={open}
      onClose={onClose}
      sx={(theme) => ({
        '& .MuiDrawer-paper': {
          ...sidebarPaperSx(theme),
          boxShadow: resolveTokens(theme).elevation.dialog,
        },
      })}
    >
      <SidebarContent {...props} />
    </Drawer>
  );
}
